import React from 'react';
import PropTypes from 'prop-types';

import SystemMessage from '../SystemMessage/SystemMessage';

const PENDING = 'pending';

/**
 * Tells the counselor when the CRM didn't get what this session produced.
 *
 * Drupal pushes to the CRM inside the JSON:API save. Everything it has to say
 * about that push used to go to Drupal's messenger, which only renders on a
 * Drupal page — so for a personalization session it was written to a queue
 * nobody ever read. The counselor finished, got a PIB link, and never learnt
 * that the person exists in the CMS only.
 *
 * Successes stay silent: the counselor only needs to act when something failed.
 *
 * Not every failure needs a person, though. Drupal offers a rejected create to
 * a retry queue and re-attempts it on cron, and marks the items it accepted
 * `retry: 'pending'`. Those show as a warning that asks for nothing — telling
 * the counselor to enter someone by hand who is about to be retried is how a
 * duplicate person gets into the CRM. A failure that was never queued, or whose
 * attempts have run out (`retry: 'exhausted'`), keeps the red error and the
 * enter-this-manually remedy.
 *
 * The sentence itself is written by Drupal and arrives in `item.message`, so
 * both states read correctly without this component knowing the wording.
 */
const CrmPushStatusMessage = ( { pushStatus } ) => {
  const failures = ( pushStatus?.items || [] ).filter( ( item ) => !item.ok );

  if ( failures.length === 0 ) {
    return null;
  }

  const crm = pushStatus.crm || 'the CRM';

  const toItem = ( failure ) => ( {
    key: `${failure.resident_uuid}-${failure.type}`,
    testId: `crm-push-failure:${failure.type}`,
    content: (
      <>
        <span className="personalization__crm-push-status-name">
          {failure.resident_name}
          :
        </span>
        {' '}
        {failure.message}
      </>
    ),
  } );

  const pending = failures.filter( ( failure ) => failure.retry === PENDING );
  const unrecoverable = failures.filter( ( failure ) => failure.retry !== PENDING );

  // Both lists can be non-empty at once: one change type can be queued while
  // another on the same resident is not, so they are rendered as two messages
  // rather than one whose heading would be wrong for half its items.
  return (
    <>
      {unrecoverable.length > 0 && (
        <SystemMessage
          status="error"
          title={`Not everything reached ${crm}`}
          items={unrecoverable.map( toItem )}
          testId="crm-push-status:failed"
        />
      )}

      {pending.length > 0 && (
        <SystemMessage
          status="warning"
          title={`Still reaching ${crm}`}
          items={pending.map( toItem )}
          testId="crm-push-status:pending"
        />
      )}
    </>
  );
};

CrmPushStatusMessage.propTypes = {
  pushStatus: PropTypes.shape( {
    crm: PropTypes.string,
    pushed: PropTypes.bool,
    items: PropTypes.arrayOf( PropTypes.shape( {
      resident_uuid: PropTypes.string,
      resident_name: PropTypes.string,
      type: PropTypes.string,
      ok: PropTypes.bool,
      retry: PropTypes.string,
      message: PropTypes.string,
    } ) ),
  } ),
};

CrmPushStatusMessage.defaultProps = {
  pushStatus: null,
};

export default CrmPushStatusMessage;
